import AppResource from "./Data/AppResource";
import axios from 'axios';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import ApiResponse from "./Api/ApiResponse";

class SoftiaEngine {

    constructor(engineConfig) {
        this._appResources = {};
        this.rawConfig = engineConfig;
    }

    /**
     *
     * @param value
     */
    set rawConfig(value) {
        const rawConfig = cloneDeep(value);

        this._rawConfig = {
            ...this.defaultConfig(),
            ...rawConfig
        };

        this._debug = this._rawConfig.debug;
        this._xhrLib = this._rawConfig.xhrLib;
        this._apiHost = this._rawConfig.apiHost;
        this._apiVersion = this._rawConfig.apiVersion;
        this._apiPrefix = this._rawConfig.apiPrefix;
        this._environments = this._rawConfig.environments;
        this._defaultEnvironment = this._rawConfig.defaultEnvironment;
        for (let i = 0; i < this._rawConfig.appResources.length; i++) {
            this.registerAppResource(this._rawConfig.appResources[i]);
        }

    }

    get rawConfig() {
        return this._rawConfig;
    }

    /**
     * @returns {*|string}
     */
    get defaultEnvironment() {
        const current = this._defaultEnvironment;
        if (current === null && !isEmpty(this._environments)) {
            // noinspection LoopStatementThatDoesntLoopJS
            for (let envName in this._environments) {
                this._defaultEnvironment = envName;
                break;
            }
        }
        return this._defaultEnvironment;
    }

    /**
     *
     * @returns {*}
     */
    get debug() {
        return this._debug;
    }

    /**
     *
     * @param value
     */
    set debug(value) {
        this._debug = value;
    }

    /**
     *
     * @returns {*}
     */
    get xhrLib() {
        return this._xhrLib;
    }

    /**
     *
     * @param value
     */
    set xhrLib(value) {
        this._xhrLib = value;
    }

    /**
     * @returns {string}
     */
    get apiVersion() {
        return this._apiVersion;
    }

    /**
     * @param value
     */
    set apiVersion(value) {
        this._apiVersion = value;
    }

    /**
     * @returns {*}
     */
    get apiPrefix() {
        return this._apiPrefix;
    }

    /**
     * @param value
     */
    set apiPrefix(value) {
        this._apiPrefix = value;
    }

    /**
     * @param value
     */
    set environments(value) {
        this._environments = value;
    }

    /**
     * @returns {*|null}
     */
    get apiHost() {
        if (this._apiHost === null) {
            const envHost = this.environmentHost();
            if (envHost !== null) {
                this._apiHost = envHost;
            }
        }
        return this._apiHost;
    }

    /**
     *
     * @returns {*}
     */
    get appResources() {
        return this._appResources;
    }

    /**
     * @param engineConfig
     */
    config(engineConfig) {
        this.rawConfig = engineConfig;
    }

    /**
     * @returns {{defaultEnvironment: null, apiVersion: string, apiPrefix: string, environments: {}, apiHost: null, appResources: []}}
     */
    defaultConfig() {
        const defaults = {
            apiVersion: 'v1',
            apiPrefix: 'api',
            debug: false,
            apiHost: null,
            xhrLib: axios,
            appResources: [],
            environments: {},
            defaultEnvironment: null,
        };
        for (let param in defaults) {
            if (!defaults.hasOwnProperty(param)) {
                continue;
            }
            if (typeof this[param] !== "undefined") {
                defaults[param] = this[param];
            }
        }
        return defaults;
    }

    /**
     * @param environment
     * @returns {string|null}
     */
    environmentHost(environment = null) {
        if (environment === null) {
            environment = this.defaultEnvironment;
        }
        if (environment === null) {
            return null;
        }
        if (
            this._environments.hasOwnProperty(environment) &&
            this._environments[environment].hasOwnProperty('apiHost')
        ) {
            return this._environments[environment].apiHost;
        }
        return null;
    }

    /**
     *
     * @param appResourceClass
     */
    registerAppResource(appResourceClass) {
        if (Array.isArray(appResourceClass)) {
            for (let i = 0; i < appResourceClass.length; i++) {
                this.registerAppResource(appResourceClass[i]);
            }
            return;
        }
        if (
            typeof appResourceClass !== 'function' ||
            !(appResourceClass.prototype instanceof AppResource)
        ) {
            return;
        }


        const inst = new appResourceClass();
        this._appResources[inst.appResourceType()] = appResourceClass;
    }

    /**
     * @param appResourceType
     * @returns {AppResource}
     */
    appResourceClass(appResourceType) {
        let resourceClass = AppResource;
        for (let resourceType in this.appResources) {
            if (!this.appResources.hasOwnProperty(resourceType)) {
                continue;
            }
            if (appResourceType === resourceType) {
                resourceClass = Engine.appResources[resourceType];
                break;
            }

            if (appResourceType.toLowerCase() === resourceType.toLowerCase()) {
                resourceClass = Engine.appResources[resourceType];
                break;
            }
        }
        return resourceClass;
    }

    /**
     * @param path
     * @param environment
     * @returns {string}
     */
    apiUrl(path, environment = null) {
        let envHost = this.apiHost;
        if (environment !== null) {
            envHost = this.environmentHost(environment);
        }

        let apiUrl = envHost === null ? '' : envHost +
            '/' + this.apiPrefix +
            '/' + this.apiVersion;

        if (typeof path === "string") {
            apiUrl = apiUrl + '/' + path;
        }

        return apiUrl;
    }

    /**
     *
     * @param url
     * @param params
     * @returns {Promise<ApiResponse>}
     */
    async post(url, params, config) {
        const response = await this.xhrLib.post(url, params, config);
        return new ApiResponse(response.data);
    }

    /**
     *
     * @param url
     * @param params
     * @returns {Promise<ApiResponse>}
     */
    async get(url, params) {
        const response = await this.xhrLib.get(url, params);
        return new ApiResponse(response.data);
    }

    /**
     *
     * @param url
     * @param params
     * @returns {Promise<ApiResponse>}
     */
    async delete(url, params) {
        const response = await this.xhrLib.delete(url, params);
        return new ApiResponse(response.data);
    }
}

const Engine = new SoftiaEngine();

export default Engine;
